import type { FC, ReactNode } from 'react'

import { ArrowRight, GraduationCap, Layers, Users } from 'lucide-react'
import { buttonClass } from '@/lib/button'
import SignInCard from '@/components/landing/sign-in-card'

const HIGHLIGHTS = [
    { label: "Live client projects", icon: Layers },
    { label: "Trainer-led batches", icon: Users },
    { label: "B.Tech, Degree and Diploma", icon: GraduationCap }
]

const HeroSection: FC = (): ReactNode => {
    return (
        <section id='hero' aria-labelledby='hero-title' className='relative isolate overflow-hidden bg-ink text-white'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-10'>
                <div className='hero-grid absolute inset-0' />
                <div className='absolute -left-48 -top-48 size-[40rem] rounded-full bg-primary/35 blur-[120px]' />
                <div className='absolute -bottom-56 -right-48 size-[36rem] rounded-full bg-teal/20 blur-[120px]' />
            </div>

            <div className='mx-auto grid max-w-7xl items-center gap-12 px-5 pb-32 pt-12 sm:px-6 sm:pt-16 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16 lg:px-8 lg:pb-40 lg:pt-20'>
                <div className='motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-4 motion-safe:duration-700'>
                    <p className='inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/80'>
                        <span className='relative flex size-2'>
                            <span className='absolute inline-flex size-full rounded-full bg-teal opacity-60 motion-safe:animate-ping' />
                            <span className='relative inline-flex size-2 rounded-full bg-teal' />
                        </span>
                        Academic portal for Webify students and trainers
                    </p>

                    <h1 id='hero-title' className='mt-6 text-[2.6rem] font-semibold leading-[1.04] sm:text-6xl lg:text-[4.25rem]'>
                        Learn tech by building{' '}
                        <span className='bg-linear-to-r from-teal via-teal to-violet-300 bg-clip-text text-transparent'>
                            real software.
                        </span>
                    </h1>

                    <p className='mt-6 max-w-xl text-lg leading-relaxed text-white/70'>
                        Trainer-led programs and internships from a software company with 10+ years of client work across the USA, UK and India. Already enrolled?{' '}
                        <a href='#sign-in' className='font-semibold text-white underline decoration-teal/60 underline-offset-4 transition-colors hover:decoration-teal'>
                            Sign in to your portal
                        </a>
                        .
                    </p>

                    <div className='mt-8 flex flex-col gap-3 sm:flex-row'>
                        <a href='#register' className={buttonClass('accent', 'lg', 'group')}>
                            Register for a program
                            <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                        </a>

                        <a href='#programs' className={buttonClass('outline-light', 'lg')}>
                            Explore programs
                        </a>
                    </div>

                    <ul className='mt-10 flex flex-wrap gap-x-6 gap-y-3 text-sm text-white/70'>
                        {HIGHLIGHTS.map((item) => (
                            <li key={item.label} className='flex items-center gap-2'>
                                <item.icon className='size-4 text-teal' aria-hidden='true' />
                                {item.label}
                            </li>
                        ))}
                    </ul>
                </div>

                <SignInCard />
            </div>
        </section>
    )
}

export default HeroSection
